import { ThemeOptions, alpha, getContrastRatio } from '@mui/material';

import { deepmerge } from '@mui/utils';
import { typographyThemeOptions } from './typography';

// Colors
const typographyPrimary = '#000000dd';
const typographySecondary = '#00000099';
const blackColor = '#353535';

const darkGreyColor = '#9A9A9A';
const lightGreyColor = '#EDEDED';
const whiteColor = '#FFF';

const linkColor = '#0038FF';

const primaryColor = '#673AB7';
const secondaryColor = '#9c27b0';

const shadow = '#00000033';
const backgroundColor = '#fff';

// const dividerColor = '#000000de';

const successColor = '#2E7D32';

const warningColor = '#EF6C00';

const criticalColor = '#FF7171';
const criticalLightColor = '#CE0000';
const criticalDarkColor = '#7E0000';
const calibrationBannerColor = 'rgba(255, 216, 118, 0.5)';

// Analytic Charts
const chartTheme = [
    '#5D62B5',
    '#3FC3BE',
    '#F2726F',
    '#F1D302',
    '#415D43',
    '#BE3F3F',
    '#02D3F1',
    '#3FBE3F',
    '#F1D302',
    '#5D415D',
    '#0D58E2',
    '#F2726F',
    '#3FC3A4',
    '#415D43',
    '#E2D20D',
    '#800080',
];

// Borders
const lightBorder = '#e0e0e0';
const dataBlockBorder = '#dbdbdb';

// Backgrounds
const successDataBlockBG = `linear-gradient(249deg, ${alpha(successColor, 0.2)} 0%, rgba(255, 255, 255, 0) 100%)`;
const warningDataBlockBG = `linear-gradient(249deg, ${alpha(warningColor, 0.2)} 2.89%, rgba(255, 255, 255, 0) 97.34%)`;
const criticalDataBlockBG = `linear-gradient(248deg, ${alpha(criticalLightColor, 0.2)} 1.64%, rgba(206, 0, 0, 0) 98.45%)`;
const championDataBlockBG = `linear-gradient(69deg, rgba(255, 255, 255, 0) 0%, ${alpha(primaryColor, 0.2)} 97.65%)`;

const defaultTheme: ThemeOptions = {
    palette: {
        mode: 'light',
        text: {
            primary: typographyPrimary,
            secondary: typographySecondary,
        },
        background: {
            default: backgroundColor,
        },
        primary: {
            main: primaryColor,
            contrastText:
                getContrastRatio(primaryColor, '#fff') > 4.5 ? '#fff' : '#111',
        },
        neutral: {
            main: lightGreyColor,
        },
        secondary: {
            main: secondaryColor,
        },
        shades: {
            black: blackColor,
            darkGrey: darkGreyColor,
            lightGrey: lightGreyColor,
            white: whiteColor,
        },
        success: {
            main: successColor,
            contrastText:
                getContrastRatio(primaryColor, '#fff') > 4.5 ? '#fff' : '#111',
        },
        warning: {
            main: warningColor,
        },
        critical: {
            main: criticalColor,
            light: criticalLightColor,
            dark: criticalDarkColor,
        },
        border: {
            main: lightBorder,
            dataBlock: dataBlockBorder,
        },
        link: {
            main: linkColor,
        },
        dataBlockBackground: {
            success: successDataBlockBG,
            warning: warningDataBlockBG,
            critical: criticalDataBlockBG,
            champion: championDataBlockBG,
        },
        chartTheme,
        calibrationBannerBackground: {
            main: calibrationBannerColor,
        },
        shadow: {
            main: shadow,
        },
    },

    spacing: 8,

    mixins: {
        sideDrawer: {
            width: '267px',
        },
    },

    components: {
        MuiPaper: {
            defaultProps: {
                elevation: 0,
                square: true,
            },
        },
        MuiAppBar: {
            defaultProps: {
                elevation: 0,
                square: true,
                color: 'inherit',
            },
            styleOverrides: {
                root: ({ theme }) => ({
                    borderBottom: `1px solid ${theme.palette.border.main}`,
                }),
            },
        },
        MuiChip: {
            variants: [
                {
                    props: {
                        color: 'primary',
                    },
                    style: ({ theme }) => ({
                        borderColor: theme.palette.primary.main,
                    }),
                },
            ],
            styleOverrides: {
                deleteIcon: ({ theme }) => ({
                    color: theme.palette.primary.main,
                    ':hover': {
                        color: theme.palette.primary.dark,
                    },
                }),
            },
        },
        MuiDrawer: {
            styleOverrides: {
                paper: () => ({
                    boxShadow:
                        '0px 5px 6px -3px rgba(0, 0, 0, 0.20), 0px 9px 12px 1px rgba(0, 0, 0, 0.14), 0px 3px 16px 2px rgba(0, 0, 0, 0.12);',
                }),
                root: ({ theme }) => ({
                    '.MuiListItemButton-root:hover': {
                        backgroundColor: alpha(
                            theme.palette.primary.main,
                            0.08,
                        ),
                        '.MuiListItemIcon-root': {
                            color: theme.palette.primary.main,
                        },
                    },
                    '.MuiListItemButton-root.Mui-selected': {
                        backgroundColor: alpha(
                            theme.palette.primary.main,
                            0.08,
                        ),
                        '&:hover': {
                            backgroundColor: alpha(
                                theme.palette.primary.main,
                                0.12,
                            ),
                        },
                        '.MuiListItemIcon-root': {
                            color: theme.palette.primary.main,
                        },
                    },
                }),
            },
        },
    },
};

export default deepmerge(defaultTheme, typographyThemeOptions);
